import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';

export default function ProductAutoSuggest({ api, category, onSelect, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ search: query, limit: '15' });
        if (category) params.set('category', category.toLowerCase());
        const { data } = await api.get(`/products?${params}`);
        setResults(data.items || []);
        setOpen(true);
      } catch {
        setResults([]);
      }
      setLoading(false);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, category, api]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (product) => {
    setQuery('');
    setOpen(false);
    setResults([]);
    onSelect(product);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="product-autosuggest-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder || 'Search product catalog...'}
          className="w-full pl-7 pr-2 py-1.5 border border-amber-300 rounded text-xs bg-amber-50/30 placeholder:text-slate-400 focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
        />
        {loading && <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div data-testid="product-suggest-dropdown" className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {results.map(p => (
            <button
              key={p._id}
              data-testid={`suggest-product-${p._id}`}
              onClick={() => handleSelect(p)}
              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-slate-100 last:border-0 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-800 truncate">{p.name}</span>
                <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">{p.supplier}</span>
              </div>
              <div className="flex gap-3 mt-0.5 text-[10px] text-slate-500">
                <span>Mat: ${(p.materials_rate || 0).toFixed(2)}</span>
                <span>Lab: ${(p.labour_rate || 0).toFixed(2)}</span>
                {p.buy_cost_rate > 0 && <span>Buy: ${p.buy_cost_rate.toFixed(2)}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && query.length >= 2 && !loading && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-400 text-center">
          No products found for "{query}"
        </div>
      )}
    </div>
  );
}
